
import { useState, useRef } from 'react';
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const useAvatarManagement = () => {
  const [customAvatarFile, setCustomAvatarFile] = useState<File | null>(null);
  const [customAvatarPreview, setCustomAvatarPreview] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size too large. Please upload an image smaller than 3MB.",
        variant: "destructive"
      });
      return;
    }
    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/i)) {
      toast({
        title: "Error",
        description: "Invalid file type. Please upload an image file (JPEG, PNG, GIF, WEBP).",
        variant: "destructive"
      });
      return;
    }
    setCustomAvatarFile(file);
    const previewUrl = URL.createObjectURL(file);
    setCustomAvatarPreview(previewUrl);
  };

  const uploadCustomAvatar = async (userId: string): Promise<string | null> => {
    if (!customAvatarFile) return null;
    setIsUploading(true);
    try {
      const filePath = `${userId}/${Date.now()}-${customAvatarFile.name}`;
      const { data, error } = await supabase.storage
        .from('user_avatars')
        .upload(filePath, customAvatarFile, {
          cacheControl: '3600',
          upsert: true
        });
      if (error) {
        console.error('Error uploading avatar:', error);
        return 'https://api.dicebear.com/7.x/adventurer/svg?seed=default';
      }
      const { data: urlData } = supabase.storage
        .from('user_avatars')
        .getPublicUrl(filePath);
      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: "Warning",
        description: "Failed to upload profile picture. Using default avatar instead.",
        variant: "destructive"
      });
      return 'https://api.dicebear.com/7.x/adventurer/svg?seed=default';
    } finally {
      setIsUploading(false);
    }
  };

  const clearCustomAvatar = () => {
    if (customAvatarPreview) {
      URL.revokeObjectURL(customAvatarPreview);
    }
    setCustomAvatarFile(null);
    setCustomAvatarPreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return {
    customAvatarFile,
    setCustomAvatarFile,
    customAvatarPreview,
    setCustomAvatarPreview,
    isUploading,
    setIsUploading,
    fileInputRef,
    handleFileChange,
    uploadCustomAvatar,
    clearCustomAvatar
  };
};
